import { useContext, useEffect, useMemo, useState } from "react";
import TopNav from "ui/dashboard/topnav";
import Cookies from "js-cookie";
import { SvgForward, SvgLive } from "ui/svgs";
import { AuthContext, User } from "context/AuthContext";
import { Button, Table, Tooltip } from "antd";
import { loginForAdmin } from "../user/service";
import { getCamerasForAdmin } from "./service";
import { useSearchParams, useLocation } from "react-router-dom";
import { showToast } from "ui/components/common";
import type { TableColumnsType } from "antd";
import { useNavigate } from "react-router-dom";
import Search from "ui/search";
import clsx from "clsx";
import useWsSender from "lib/wsSenderHook";

type DataType = {
  customerName: string;
  dealerName: string;
  id: number;
  macAddress: string;
  name: string;
  sn: string;
  customerOwnerId: number;
  orgId: number;
  cameraIp: string;
  cameraStatus: string;
  deviceId: string;
  siteId: number;
  siteName: string;
  nvrSn: string;
  type: "camera" | "edge_camera";
};
export default function Page() {
  const { setUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [PageSize, setPageSize] = useState(20);
  const [totalPage, setTotalPage] = useState(0);
  const [cameras, setCameras] = useState<DataType[]>([]);

  const id = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("id") || "";
  }, [location]);

  const macAddress = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("macAddress") || "";
  }, [location]);

  const nvrSn = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("nvrSn") || "";
  }, [location]);
  const siteName = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get("siteName") || "";
  }, [location]);
  useEffect(() => {
    if (current !== 0) {
      setCurrent(0);
      return;
    }
    getCamerasForAdmin(+id, macAddress, nvrSn, PageSize, 0, siteName).then(
      (res) => {
        if (res?.status !== 200) {
          showToast(1, "Failed to get camera list, please try again later");
          return;
        }
        const data = res.data;
        setCameras(data?.data);
        setTotalPage(data?.page?.total);
      },
    );
  }, [id, macAddress, nvrSn, PageSize, siteName]);

  useEffect(() => {
    getCamerasForAdmin(
      +id,
      macAddress,
      nvrSn,
      PageSize,
      current,
      siteName,
    ).then((res) => {
      if (res?.status !== 200) {
        showToast(1, "Failed to get camera list, please try again later");
        return;
      }
      const data = res.data;
      setCameras(data?.data);
      setTotalPage(data?.page?.total);
    });
  }, [current]);

  const login = async (userId, redirectUrl) => {
    const res = await loginForAdmin(userId);
    if (res?.status !== 200) {
      showToast(1, "Failed to login as user, please try again later");
      return;
    }
    const token = res?.data.token;
    const accountName = res?.data.user.organization.name;
    Cookies.set("orgId", res?.data.user?.organization?.id, { expires: 1 });
    Cookies.set("userId", res?.data.user?.id, { expires: 1 });
    Cookies.set("token", token, {
      expires: 1,
      domain: "." + window.location.hostname,
    });
    Cookies.set("accountName", accountName, { expires: 1 });
    Cookies.set(
      "enableMonitoring",
      res?.data.user.organization.enableMonitoring,
      {
        expires: 1,
      },
    );
    Cookies.set(
      "name",
      res?.data.user.firstName + " " + res?.data.user.lastName,
    );
    window.parent.postMessage({ token: token, type: "loginClick" }, "*");
    const user: User = {
      id: res?.data.user.id,
      email: res?.data.user.email,
      accountName: res?.data.user.organization.name,
      orgId: res?.data.user.organization.id,
      role: res?.data.user.role.name,
      token: res?.data.token,
      name: res?.data.user.firstName + " " + res?.data.user.lastName,
      phoneNumber: res?.data.user.phoneNumber,
      organization: res?.data.user.organization,
    };

    setUser(user);
    navigate(redirectUrl);
    window.location.reload();
  };
  const { sendWsRequest } = useWsSender();
  const forwardCamera = async (id, orgId, type) => {
    try {
      const res: any = await sendWsRequest(
        "device.remote_service.open",
        {
          deviceOpenRemoteService: {
            deviceId: id.toString(),
            deviceType: type,
          },
        },
        { orgId },
      );
      const query =
        "?expiredAt=" +
        res.deviceOpenRemoteServiceReply.cookie.expiredAt +
        "&name=" +
        res.deviceOpenRemoteServiceReply.cookie.name +
        "&value=" +
        res.deviceOpenRemoteServiceReply.cookie.value;
      window.open(
        "https://" + res.deviceOpenRemoteServiceReply.domain + query,
        "_blank",
      );
    } catch (e) {
      console.error(e);
      showToast(1, "Forward failed");
    }

    return;
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "ID",
      width: 80,
      dataIndex: "id",
    },
    {
      title: "Name",
      render: (e) => (
        <div className="overflow-hidden break-words break-all font-semibold">
          <span
            className={clsx(
              "inline-block w-2 h-2 rounded-full mr-1 relative -top-0.5",
              e.cameraStatus === "online" ? "bg-teal-600" : "bg-red-500",
            )}
          />
          <span>{e?.name}</span>
        </div>
      ),
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
    },
    {
      title: "Site Name",
      dataIndex: "siteName",
    },
    {
      title: "Device Id",
      dataIndex: "deviceId",
    },
    {
      title: "Device SN",
      render: (_, e) => {
        if (e?.type === "edge_camera") {
          return e?.sn;
        } else {
          return e?.nvrSn;
        }
      },
    },
    {
      title: "Mac Address",
      dataIndex: "macAddress",
    },

    {
      title: "IP Address",
      dataIndex: "cameraIp",
    },

    {
      title: "Camera Model",
      dataIndex: "cameraModel",
    },
    {
      title: "Action",
      render: (e) => (
        <div className="overflow-hidden break-words break-all font-semibold flex gap-1 flex-wrap">
          <Tooltip title="View Live Streaming">
            <button
              className="fill-[#222222] "
              onClick={() =>
                login(
                  e.customerOwnerId,
                  "/dashboard/streaming?siteId=" +
                    e.siteId +
                    "&cameraId=" +
                    e.id,
                )
              }
            >
              <SvgLive width="20" className="" />
            </button>
          </Tooltip>
          <Tooltip title="Forward">
            <button
              className="fill-[#222222] "
              onClick={() => {
                const id = e?.type === "edge_camera" ? e?.deviceId : e?.id;
                forwardCamera(id, e.orgId, e?.type);
              }}
            >
              <SvgForward width="16" />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "login",
      width: 200,
      render: (e) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => login(e.dealerOwnerId, "/dashboard")}
            >
              Login To DealerOwner(Id:{e.dealerOwnerId})
            </Button>
            <Button
              type="link"
              onClick={() => login(e.customerOwnerId, "/dashboard")}
            >
              Login To CustomerOwner(Id:{e.customerOwnerId})
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <div className="">
      <TopNav title="Cameras" />
      <div className="flex-grow pr-6 pb-6">
        <div className="w-full p-6 rounded-3xl bg-white">
          <div className="flex gap-3 mb-4">
            <div className="w-64 ">
              <Search placeholder="Search ID" query="id" />
            </div>
            <div className="w-64 ">
              <Search placeholder="Search Mac Address" query="macAddress" />
            </div>
            <div className="w-64 ">
              <Search placeholder="Search NVR SN" query="nvrSn" />
            </div>
            <div className="w-64 ">
              <Search placeholder="Search Site Name" query="siteName" />
            </div>
          </div>
          <Table
            className="w-full pr-2"
            style={{ overflowX: "auto", maxHeight: "calc(100vh - 190px)" }}
            columns={columns}
            dataSource={cameras}
            pagination={{
              showTotal: (total) => `Total ${total} items`,
              total: totalPage,
              onChange: (page) => setCurrent(page - 1),
              pageSize: PageSize,
              current: current + 1,
              onShowSizeChange: (_, size) => {
                setCurrent(0);
                setPageSize(size);
              },
            }}
            rowClassName={() => "no-row-border"}
          />
        </div>
      </div>
    </div>
  );
}
